import { useId } from "react";
import { Input } from "components/ui/input";
import { Text } from "components/text";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { cn } from "utils";
import { ComparisonOperatorDropdown } from "./ComparisonOperatorDropdown";

interface NumericComparisonInputProps {
  valueName: string;
  comparisonName: string;
  ariaLabel: string;
  id?: string;
  error?: string;
  max?: number;
  suffix?: string;
  normalizeValue?: (value: number | undefined) => number | undefined;
}

export const NumericComparisonInput = ({
  valueName,
  comparisonName,
  ariaLabel,
  id,
  error,
  max,
  suffix,
  normalizeValue,
}: NumericComparisonInputProps) => {
  const { t } = useTranslation(["general"]);
  const { register } = useFormContext();
  const errorId = useId();

  return (
    <div className="flex flex-col">
      <span className="group flex flex-row focus-within:rounded-[8px] focus-within:ring-3 focus-within:outline-solid focus-within:outline-1 focus-within:outline-primary focus-within:border-0 focus-within:ring-offset-0 focus-within:ring-primary-100">
        <ComparisonOperatorDropdown name={comparisonName} />

        <Input
          id={id}
          type="number"
          min={0}
          max={max}
          placeholder={t("general:filters.numericPlaceholder")}
          className={cn(
            "rounded-[8px] rounded-l-none text-sm flex-1 min-w-0 text-left outline-hidden shadow-none border-l-0 group-focus-within:border-0",
            suffix && "rounded-r-none border-r-0",
          )}
          aria-label={ariaLabel}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          {...register(valueName, {
            setValueAs: (rawValue: string) => {
              const parsed = Number(rawValue);
              const numericValue =
                rawValue === "" || Number.isNaN(parsed) ? undefined : parsed;
              return normalizeValue
                ? normalizeValue(numericValue)
                : numericValue;
            },
          })}
        />
        {suffix && (
          <span className="flex h-10 items-center rounded-[8px] rounded-l-none border border-l-0 border-secondary-300 bg-white px-3 group-focus-within:border-0">
            <Text as="span" variant="body">
              {suffix}
            </Text>
          </span>
        )}
      </span>
      {error && (
        <Text id={errorId} variant="errorSmall">
          {error}
        </Text>
      )}
    </div>
  );
};
